import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Song, StreamingLink } from "../../model/launchpad";
import placeholder from "url:asset/song-placeholder.svg";
import Sheet from "../ui/Sheet";
import { MusicIcon, XIcon } from "../ui/icons";
import { PlatformIcon, platformDisplayOrder } from "./platformIcons";

// Web port of SongPlatformsView
// (LibraryFeature/View/Screen/SongPlatformsView.swift).
//
// Presented whenever a song is tapped, from anywhere — see SongPlatformsProvider.

/** SongPlatformsView truncates both lines at 30 characters. */
const MAX_LABEL_LENGTH = 30;

function truncate(value: string): string {
  return value.length > MAX_LABEL_LENGTH
    ? `${value.slice(0, MAX_LABEL_LENGTH)}...`
    : value;
}

export interface SongPlatformsSheetProps {
  song: Song | null;
  onClose: () => void;
}

export default function SongPlatformsSheet(props: SongPlatformsSheetProps) {
  const { t } = useTranslation("songPlatforms");

  // The sheet animates out after props.song clears, so keep rendering the last
  // song until it is hidden — otherwise the panel slides away empty.
  const [lastSong, setLastSong] = useState<Song | null>(null);

  useEffect(() => {
    if (props.song) setLastSong(props.song);
  }, [props.song]);

  const song = props.song ?? lastSong;

  const links: StreamingLink[] = song
    ? [...song.attributes.streamingLinks].sort(
        (a, b) => platformDisplayOrder(a.platform) - platformDisplayOrder(b.platform)
      )
    : [];

  return <Sheet
    open={props.song !== null}
    onClose={props.onClose}
    label={props.song ? props.song.attributes.title : t("label")}
  >
    {song && <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Pinned: everything else in the sheet scrolls beneath it. */}
      <button
        type="button"
        onClick={props.onClose}
        aria-label={t("close")}
        className="absolute left-4 top-4 z-20 flex size-9 items-center justify-center rounded-full bg-gray-200/80 text-gray-700 backdrop-blur-sm transition-opacity duration-200 hover:opacity-75"
      >
        <XIcon />
      </button>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 pb-6">
        {/* ZStack(alignment: .top): 170pt artwork with the capsule label
            straddling its bottom edge. `top-full` puts the capsule's top on
            that edge and -translate-y-1/2 pulls it back up by half its own
            height, so it stays centred on the edge whatever the text wraps to. */}
        <div className="relative mx-auto w-[170px] pt-4 pb-6">
          <div className="relative">
            <img
              src={song.attributes.artworkURL ?? placeholder}
              alt=""
              className="size-[170px] rounded-lg object-cover"
            />

            <div className="absolute left-1/2 top-full flex max-w-[calc(100%+3rem)] -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full bg-surface/80 px-4 py-1 text-xs shadow-sm backdrop-blur-md">
              <span className="text-gray-700"><MusicIcon className="size-3.5 shrink-0" /></span>
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-medium text-gray-900">
                  {truncate(song.attributes.title)}
                </span>
                <span className="truncate text-gray-600">
                  {truncate(song.attributes.artist.name)}
                </span>
              </span>
            </div>
          </div>
        </div>

        {links.length > 0
          ? <ul>
              {links.map((link, index) => (
                <li key={`${link.platform}-${link.url}`}>
                  {/* Divider between rows, not before the first. */}
                  {index > 0 && <hr className="border-separator" />}
                  <PlatformRow link={link} />
                </li>
              ))}
            </ul>
          : <NoLinks />}
      </div>
    </div>}
  </Sheet>
}

// StandardRowView: 60pt thumbnail, headline title, accent chevron.
function PlatformRow(props: { link: StreamingLink }) {
  return <a
    href={props.link.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex select-none items-center gap-4 py-4 transition-opacity duration-200 hover:opacity-75"
  >
    <span className="size-[60px] shrink-0 overflow-hidden rounded-xl">
      <PlatformIcon platform={props.link.platform} />
    </span>
    <span className="min-w-0 flex-1 truncate text-[17px] font-semibold text-gray-900">
      {props.link.platform}
    </span>
    <ChevronForwardIcon />
  </a>
}

// ContentUnavailableView(.songPlatformsNoLinksTitle, systemImage: "music.note.slash", ...)
function NoLinks() {
  const { t } = useTranslation("songPlatforms");

  return <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
    <MusicIcon className="size-10 text-gray-400" />
    <p className="text-[17px] font-semibold text-gray-900">{t("noLinksTitle")}</p>
    <p className="max-w-xs text-[15px] text-gray-500">{t("noLinksDescription")}</p>
  </div>
}

function ChevronForwardIcon() {
  return <svg viewBox="0 0 16 16" aria-hidden="true" className="size-4 shrink-0 text-accent">
    <path d="M5.5 2.5 11 8l-5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
}
